import { vnModule_knockoutJS } from "../../vendor/valnet/valnet_jslib/vnApp/vnModule_knockoutJS";
import { XMLHttpRequestHandler } from "../../vendor/valnet/valnet_jslib/XMLHttpRequestHandler/XMLHttpRequestHandler";
import { backfill_template } from "../components/templates/backfill_template";
import { XMLHttpRequestHandler_requestType } from "../../vendor/valnet/valnet_jslib/XMLHttpRequestHandler/XMLHttpRequestHandler";

export class backfill extends vnModule_knockoutJS{

    protected backfillTemplate: backfill_template;
    protected static readonly DEFAULT_TASKS_PAGE_SIZE: number = 50;

    protected getModuleName(){
        return "backfill";
    }

    buildContent() : Array<Element>{

        this.backfillTemplate = new backfill_template( "backfillPage", this.viewModel, this );

        return [ this.backfillTemplate.build()];
    }

    public fillPage() : void{
        this.backfillTemplate.clearPageMessage();
        this.loadOperations();
        this.loadBatches();
    }

    protected executeRequest(
        url: string,
        mode: XMLHttpRequestHandler_requestType,
        params: Array<Array<string>>,
        onSuccess: (response: any) => void,
        onError?: (message: string, statusCode: number, response?: any) => void,
        onFinally?: () => void
    ): void {
        // Shared XHR wrapper keeps parsing, error extraction, and finalization behavior
        // consistent across all backfill API calls.
        let XHRHdl = new XMLHttpRequestHandler(url, params, this);
        XHRHdl.mode = mode;

        XHRHdl.onReadyStateFunction = function(req: any) {
            return function() {
                if (req.request.readyState !== 4) {
                    return;
                }

                let response: any = {};
                try {
                    response = req.request.response ? JSON.parse(req.request.response) : {};
                } catch (e) {
                    response = {};
                }

                if (req.request.status === 200) {
                    onSuccess(response);
                    if (onFinally) {
                        onFinally();
                    }
                    return;
                }

                let message = (response && response.error && response.error.message)
                    ? response.error.message
                    : ((response && response.message) ? response.message : 'Request failed.');

                if (onError) {
                    onError(message, req.request.status, response);
                } else {
                    console.error('Backfill request failed:', req.request.status, req.request.response);
                }

                if (onFinally) {
                    onFinally();
                }
            };
        };

        XHRHdl.execute();
    }

    /**
     * Normalize all backfill API envelope parsing in one place.
     */
    protected handleApiResponse(
        response: any,
        onOk: (data: any, meta: any) => void,
        onFail?: (message: string, response: any) => void
    ): void {
        if (response && response.ok === true) {
            onOk(response.data, response.meta || {});
            return;
        }

        let message = this.getApiErrorMessage(response, 'Request failed.');
        if (onFail) {
            onFail(message, response);
        }
    }

    protected getApiErrorMessage(response: any, fallbackMessage: string): string {
        if (response && response.error && response.error.message) {
            return response.error.message;
        }

        if (response && response.message) {
            return response.message;
        }

        return fallbackMessage;
    }

    public loadOperations(): void {
        this.backfillTemplate.activateLoading();

        this.executeRequest(
            '/api/backfill/operations/list/',
            XMLHttpRequestHandler_requestType.GET,
            [],
            (response: any) => {
                this.handleApiResponse(
                    response,
                    (data: any) => {
                        this.backfillTemplate.setOperations(Array.isArray(data) ? data : []);
                    },
                    () => {
                        this.backfillTemplate.setOperations([]);
                        this.backfillTemplate.setPageMessage('Unable to load operations.');
                    }
                );
            },
            () => {
                this.backfillTemplate.setOperations([]);
                this.backfillTemplate.setPageMessage('Unable to load operations.');
            },
            () => {
                this.backfillTemplate.deactivateLoading();
            }
        );
    }

    public filterSingleGroupSelectorOnChange(elem: string): void {
        if (elem === "operationFilter") {
            let selectedOption = this.backfillTemplate.operationFilter.getTemplateViewModel().selectedOption();
            let options = this.backfillTemplate.operationFilter.getTemplateViewModel().options();
            let selectedOperation = options.find((op: any) => op.id === selectedOption);

            if (selectedOperation) {
                // Task executers are operation-scoped, so refresh each time operation changes.
                this.loadTaskExecuters(selectedOperation.name);
            } else {
                this.clearTaskExecutersSelectionState();
            }
        }
    }

    protected clearTaskExecutersSelectionState(): void {
        // Keep all "no operation selected" reset behavior in one place.
        this.backfillTemplate.setTaskExecutersLoading(false);
        this.backfillTemplate.setTaskExecutersFilterDisabled(false);
        this.backfillTemplate.setTaskExecuters([]);

        if (this.backfillTemplate.taskExecutersFilter) {
            this.backfillTemplate.taskExecutersFilter.setSelectedItems([]);
        }
    }

    protected filterMultipleSelectorOnChange(): void {
        // Task executers filter changed - no action needed, just prevent error.
    }

    protected filterSingleDateOnChange(): void {
        // Date picker changed - no immediate action required.
    }

    public loadTaskExecuters(operationName: string): void {
        // Function map:
        // Step 1 turns on request-scoped loading/disabled UI states.
        // Step 2 requests operation-scoped task executer candidates.
        // Step 3 applies normalized API envelope handling.
        // Step 4 always clears request-scoped UI states in finalizer.

        // Task executer list is operation-scoped. While it loads, keep the selector
        // visually and functionally locked to avoid stale selections.
        // Step 1: lock filter and show spinner while data is in-flight.
        this.backfillTemplate.setTaskExecutersLoading(true);
        this.backfillTemplate.setTaskExecutersFilterDisabled(true);
        this.backfillTemplate.activateLoading();

        this.executeRequest(
            '/api/backfill/taskExecuters/get/',
            XMLHttpRequestHandler_requestType.GET,
            [['operation_name', operationName]],
            (response: any) => {
                // Step 3: parse standardized API envelope and update selector rows.
                this.handleApiResponse(
                    response,
                    (data: any) => {
                        this.backfillTemplate.setTaskExecuters(Array.isArray(data) ? data : []);
                    },
                    (message: string) => {
                        this.backfillTemplate.setTaskExecuters([]);
                        this.backfillTemplate.setPageMessage(message || 'Unable to load task executers.');
                    }
                );
            },
            () => {
                this.backfillTemplate.setTaskExecuters([]);
                this.backfillTemplate.setPageMessage('Unable to load task executers.');
            },
            () => {
                // Step 4: always re-enable selector and hide spinner.
                this.backfillTemplate.setTaskExecutersLoading(false);
                this.backfillTemplate.setTaskExecutersFilterDisabled(false);
                this.backfillTemplate.deactivateLoading();
            }
        );
    }


    public submitBackfill(): void {
        // Function map:
        // Step 1 reads + validates client-side required inputs.
        // Step 2 sends create request.
        // Step 3 maps success payload to user feedback and refresh.
        // Step 4 maps validation/server errors to readable UI messages.

        // Keep submit validation minimal on client side; controller performs full validation.
        let formData = this.backfillTemplate.getFormData();
        this.backfillTemplate.clearPageMessage();

        if (!formData.operation_name || !formData.taskExecuters || formData.taskExecuters.length === 0 || !formData.dates || formData.dates.length === 0) {
            this.backfillTemplate.setPageMessage('Select an operation, at least one task executer, and at least one date.');
            return;
        }

        // Step 2: submit payload and keep form in loading state while request is running.
        this.backfillTemplate.activateLoading();

        this.executeRequest(
            '/api/backfill/generateInsertQueries/',
            XMLHttpRequestHandler_requestType.POST,
            [['data', JSON.stringify(formData)]],
            (response: any) => {
                // Step 3/4: branch on normalized envelope; success updates list + closes modal.
                this.handleApiResponse(
                    response,
                    (data: any) => {
                        this.backfillTemplate.setPageMessage('Backfill batch created successfully (Batch ID: ' + data.batchId + ').');
                        this.loadBatches();
                        this.backfillTemplate.closeCreateModal();
                    },
                    (message: string, failedResponse: any) => {
                        // Surface validation lists directly when backend provides them.
                        if (failedResponse
                            && failedResponse.error
                            && failedResponse.error.details
                            && failedResponse.error.details.errors
                            && Array.isArray(failedResponse.error.details.errors)
                            && failedResponse.error.details.errors.length > 0) {
                            this.backfillTemplate.setPageMessage(failedResponse.error.details.errors.join(' | '));
                            return;
                        }

                        this.backfillTemplate.setPageMessage(message || 'Unable to create backfill batch.');
                    }
                );
            },
            (message: string) => {
                this.backfillTemplate.setPageMessage(message || 'Unable to create backfill batch.');
            },
            () => {
                this.backfillTemplate.deactivateLoading();
            }
        );
    }


    public loadBatches(): void {
        this.executeRequest(
            '/api/backfill/batches/list/',
            XMLHttpRequestHandler_requestType.GET,
            [],
            (response: any) => {
                this.handleApiResponse(
                    response,
                    (data: any) => {
                        this.backfillTemplate.setBatches(Array.isArray(data) ? data : []);
                    },
                    (message: string) => {
                        this.backfillTemplate.setBatches([]);
                        this.backfillTemplate.setPageMessage(message || 'Unable to load backfill batches.');
                    }
                );
            },
            () => {
                this.backfillTemplate.setBatches([]);
                this.backfillTemplate.setPageMessage('Unable to load backfill batches.');
            }
        );
    }

    public loadBatchCompletion(batchId: number): void {
        this.backfillTemplate.setCompletionLoading(true);
        this.backfillTemplate.setCompletionError("");

        this.executeRequest(
            '/api/backfill/batches/' + batchId + '/completion/',
            XMLHttpRequestHandler_requestType.GET,
            [],
            (response: any) => {
                this.handleApiResponse(
                    response,
                    (data: any) => {
                        this.backfillTemplate.setCompletionDetails(data);
                    },
                    (message: string) => {
                        this.backfillTemplate.setCompletionDetails(null);
                        this.backfillTemplate.setCompletionError(message || 'Failed to load batch details.');
                    }
                );
            },
            (message: string) => {
                this.backfillTemplate.setCompletionDetails(null);
                this.backfillTemplate.setCompletionError(message || 'Failed to load batch details.');
            },
            () => {
                this.backfillTemplate.setCompletionLoading(false);
            }
        );
    }

    public loadBatchTasksPage(batchId: number, page: number = 1, limit: number = backfill.DEFAULT_TASKS_PAGE_SIZE): void {
        // Function map:
        // Step 1 normalizes requested pagination parameters.
        // Step 2 requests one page of task rows.
        // Step 3 applies timeline-divider decoration client-side.
        // Step 4 updates paginated table state or error state.

        // Guard paging inputs before request so UI never asks for invalid pages.
        let safePage = page > 0 ? page : 1;
        let safeLimit = limit > 0 ? limit : backfill.DEFAULT_TASKS_PAGE_SIZE;

        // Step 1: enter loading state for the completion tasks table.
        this.backfillTemplate.setCompletionTasksLoading(true);
        this.backfillTemplate.setCompletionTasksError("");

        this.executeRequest(
            '/api/backfill/batches/' + batchId + '/tasks/',
            XMLHttpRequestHandler_requestType.GET,
            [
                ['page', String(safePage)],
                ['limit', String(safeLimit)]
            ],
            (response: any) => {
                this.handleApiResponse(
                    response,
                    (data: any, meta: any) => {
                        // Step 3: keep API contract clean; add now-divider purely in UI layer.
                        // Add client-side timeline marker without changing server pagination contract.
                        let tasksWithNowDivider = this.addNowDividerToTasks(Array.isArray(data) ? data : []);
                        this.backfillTemplate.setCompletionTasksPage(
                            tasksWithNowDivider,
                            meta.page || safePage,
                            meta.totalPages || 0,
                            meta.totalTasks || 0
                        );
                    },
                    (message: string) => {
                        this.backfillTemplate.setCompletionTasksPage([], safePage, 0, 0);
                        this.backfillTemplate.setCompletionTasksError(message || 'Failed to load task details.');
                    }
                );
            },
            (message: string) => {
                this.backfillTemplate.setCompletionTasksPage([], safePage, 0, 0);
                this.backfillTemplate.setCompletionTasksError(message || 'Failed to load task details.');
            },
            () => {
                this.backfillTemplate.setCompletionTasksLoading(false);
            }
        );
    }

    public deleteBatch(batchId: number): void {
        let shouldDelete = window.confirm(
            'Delete this batch? In-progress tasks cannot be stopped and will keep running, but everything else will be deleted.'
        );
        if (!shouldDelete) {
            return;
        }

        this.backfillTemplate.activateLoading();
        this.backfillTemplate.clearPageMessage();

        this.executeRequest(
            '/api/backfill/batches/' + batchId + '/delete/',
            XMLHttpRequestHandler_requestType.POST,
            [],
            (response: any) => {
                this.handleApiResponse(
                    response,
                    (data: any) => {
                        let message = 'Batch deleted.';
                        if (data.deletedTasksCount !== undefined) {
                            message += ' Deleted tasks: ' + data.deletedTasksCount + '.';
                        }
                        if (data.skippedInProgressTasksCount !== undefined && data.skippedInProgressTasksCount > 0) {
                            message += ' Kept in-progress tasks: ' + data.skippedInProgressTasksCount + '.';
                        }

                        this.backfillTemplate.setPageMessage(message);
                        this.loadBatches();
                    },
                    (message: string) => {
                        this.backfillTemplate.setPageMessage(message || 'Unable to delete batch.');
                    }
                );
            },
            (message: string) => {
                this.backfillTemplate.setPageMessage(message || 'Unable to delete batch.');
            },
            () => {
                this.backfillTemplate.deactivateLoading();
            }
        );
    }

    protected addNowDividerToTasks(tasks: Array<any>): Array<any> {
        let nowTimestamp = Date.now();
        let tasksWithDivider = tasks.map((task: any) => {
            return Object.assign({}, task, { hasNowDivider: false });
        });

        // Mark the row immediately before "now" so the UI can render a timeline divider.
        for (let index = 0; index < tasksWithDivider.length - 1; index++) {
            let currentTaskTimestamp = this.parseDateTimeToTimestamp(tasksWithDivider[index].dateScheduled);
            let nextTaskTimestamp = this.parseDateTimeToTimestamp(tasksWithDivider[index + 1].dateScheduled);

            if (currentTaskTimestamp === null || nextTaskTimestamp === null) {
                continue;
            }

            if (currentTaskTimestamp <= nowTimestamp && nextTaskTimestamp > nowTimestamp) {
                tasksWithDivider[index].hasNowDivider = true;
                break;
            }
        }

        return tasksWithDivider;
    }

    protected parseDateTimeToTimestamp(dateTime: any): number | null {
        if (!dateTime || typeof dateTime !== 'string') {
            return null;
        }

        let normalizedDateTime = dateTime.trim().replace(' ', 'T');
        if (normalizedDateTime === '') {
            return null;
        }

        let timestamp = Date.parse(normalizedDateTime);
        if (isNaN(timestamp)) {
            return null;
        }

        return timestamp;
    }
}



// WEBPACK FOOTER //
// ./js/modules/backfill.ts